import React, { useEffect, useState } from 'react';
import AccountigSuiteInstruction from './AccountingSuiteInstruction';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import { INTEGRATION_ROUTES } from '../../../constants/constants';
import useGarbageCollector from '../../../../../libs/garbagecollector/garbageCollectorHooks';
import { getAccountingSuiteUrl, connectToAccountingSuite, getFormAndTaxYearList, getLoadImportData, getFormSetting, disconnectAccountingSuite } from './AccountingSuiteSaga';
import { componentKey as  accountingsuitecomponentkey} from './AccountingSuiteSlice';
import Button from '../../../../components/button/Button';
import { BUTTON_TYPE } from '../../../../components/button/constants';
import Loadable from '../../../../components/loadable/Loadable';
import RichGrid from '../../../../components/richgrid/RichGrid';
import SelectDropdown from '../../../../components/dropdown/selectdropdown/SelectDropdown';
import { VEC_ICON_NAME } from '../../../../components/icons/constants';
import styles from './AccountingSuite.styles';
import { useDispatch, useSelector } from 'react-redux';
import { INTEGRATION_CONSTANTS, INTEGRATION_WRAPPER_LOADING_STATES_MESSAGE_MAP, INTEGRATION_WRAPPER_LOADING_STATES, SOURCE_CONSTANTS, FORM_IDS } from '../constants';
import { useSearchParams } from 'react-router-dom';
import ImportDataGridView from '../../../components/importdatagridview/ImportDataGridView';

export default function AccountingSuite() {
    useGarbageCollector(accountingsuitecomponentkey);
    const [taxYearOptions, setTaxYearOptions] = useState([]);
    const [formOptions, setFormOptions] = useState([]);
    const [selectedFormMap, setSelectedFormMap] = useState({});
    const [taxYearMap, setTaxYearMap] = useState({});
    const [formattedTenantList, setFormattedTenantList] = useState([]);
    const [selectedForm, setSelectedForm] = useState({});
    const [selectedTaxYear, setSelectedTaxYear] = useState({});
    const dispatch = useDispatch();
    const [queryParams, setQueryParams] = useSearchParams();
    const {loadingState, formAndTaxYearList, authorizationUrl, tenantList, formSettings, isImportDataGridViewLoaded, importData } = useSelector(state => state[accountingsuitecomponentkey]);

    const breadcrumbsItems = [
        { text: 'Home', link: '/' },
        { text: 'Import', link: `/${INTEGRATION_ROUTES.IMPORT}` },
        { text: 'Entrata' }
    ];

    useEffect(() => {
        dispatch(getFormAndTaxYearList());
    }, []);

    useEffect(() => {
        if (authorizationUrl) {
            window.location.href = authorizationUrl;
        }
    }, [authorizationUrl]);

    useEffect(() => {
        if (formAndTaxYearList) {
            setTaxYearOptions(formAndTaxYearList.TaxYear);
            setFormOptions(formAndTaxYearList.FormType);
        }
    }, [formAndTaxYearList]);

    useEffect(() => {
        const handleQueryParams = () => {
            switch (true) {
                case queryParams.has(INTEGRATION_CONSTANTS.URL_CODE):
                    const accountingSuiteToken = queryParams.get(INTEGRATION_CONSTANTS.URL_CODE);
                    const code = accountingSuiteToken.split('?')[0];
                    dispatch(connectToAccountingSuite({ code: code }));
                    queryParams.delete(INTEGRATION_CONSTANTS.URL_CODE);
                    if (queryParams.has(INTEGRATION_CONSTANTS.URL_SCOPE)) {
                        queryParams.delete(INTEGRATION_CONSTANTS.URL_SCOPE);
                    }
                    setQueryParams(queryParams);
                    dispatch(getFormAndTaxYearList());
                    break;
                case queryParams.has(INTEGRATION_CONSTANTS.URL_ERROR):
                    queryParams.delete(INTEGRATION_CONSTANTS.URL_ERROR);
                    setQueryParams(queryParams);
                    break;
                case queryParams.has(INTEGRATION_CONSTANTS.URL_ERROR_DESCRIPTION):
                    queryParams.delete(INTEGRATION_CONSTANTS.URL_ERROR_DESCRIPTION);
                    setQueryParams(queryParams);
                    break;
            }
        };
    
        handleQueryParams();
    }, []);

    useEffect(() => {
        const fetchFormattedTenantData = () => {
            if (tenantList) {
                const formattedList = tenantList.map(tenant => ({
                    TenantName: tenant.TenantName,
                    TenantId: tenant.TenantId
                }));
                setFormattedTenantList(formattedList);
            }
            else {
                setFormattedTenantList([]);
            }
        };

        fetchFormattedTenantData();
    }, [tenantList]);

    useEffect(() => {
        if (importData) {
            dispatch(getFormSetting({ formId: selectedForm, year: selectedTaxYear }));
        }
    }, [importData]);

    const handleDataLoad = (tenantId) => {
        if (tenantId && taxYearMap[tenantId] && selectedFormMap[tenantId]) {
            dispatch(getLoadImportData({
                integrationImportRequestDto: {
                    TaxYear: taxYearMap[tenantId],
                    FormName : selectedFormMap[tenantId],
                    BusinessId : tenantId,
                    Source : SOURCE_CONSTANTS.ACCOUNTINGSUITE_SOURCE
                }
            }));
            setSelectedForm(selectedFormMap[tenantId]);
            setSelectedTaxYear(taxYearMap[tenantId]);
        } else {
            dispatch(addNotifications({
                heading: "Required fields missing",
                message: "Please choose tax year and form type to proceed"
            }));
        }
    };

    const handleDisconnect = (tenantId) => {
        if(tenantId){
            dispatch(disconnectAccountingSuite({
                removedTenant: { idForTenant: tenantId },
                tenants: tenantList
            }));
        }
    };

    const gridColumns = [
        {
            header: 'Teanant Name',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <span>{row?.TenantName}</span>
                </div>
            )
        },
        {
            header: 'Tax Year',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <SelectDropdown
                        label=""
                        placeHolder="Select taxYear"
                        name="taxYear"
                        value={{ label: taxYearMap[row?.TenantId] || '', value: taxYearMap[row?.TenantId] || '' }}
                        onSelectCb={(name, selectedOption) => setTaxYearMap(prevState => ({
                            ...prevState,
                            [row.TenantId]: selectedOption.value
                        }))}
                        suffixProps={{ icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET }}
                        options={taxYearOptions}
                        dropdownMenuStyle={styles.dropdownWidth}
                        dropdownContainerStyles={styles.dropdownWidth}
                    />
                </div>
            )
        },
        {
            header: 'Form Type',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <SelectDropdown
                        label=""
                        placeHolder="Select Form"
                        name="selectedForm"
                        value={{ label: selectedFormMap[row?.TenantId] || '', value: selectedFormMap[row?.TenantId] || '' }}
                        onSelectCb={(name, selectedOption) => setSelectedFormMap(prevState => ({
                            ...prevState,
                            [row.TenantId]: selectedOption.value
                        }))}
                        suffixProps={{ icon: VEC_ICON_NAME.ICON_DROPDOWN_CARET }}
                        options={formOptions}
                        dropdownMenuStyle={styles.dropdownWidth}
                        dropdownContainerStyles={styles.dropdownWidth}
                    />
                </div>
            )
        },
        {
            header: 'Actions',
            renderLogic: (row) => (
                <div style={styles.gridRecordsMargin}>
                    <Button
                        type={BUTTON_TYPE.PRIMARY}
                        title="DataLoad"
                        onClickCb={() => handleDataLoad(row?.TenantId)}
                    >
                        Load Data
                    </Button>
                    <Button
                        type={BUTTON_TYPE.PRIMARY}
                        title="Disconnect"
                        onClickCb={() => handleDisconnect(row?.TenantId)}
                    >
                        Disconnect
                    </Button>
                </div>
            )
        }
    ];

    return (
        <>
            <Breadcrumbs items={breadcrumbsItems} />
            {(formSettings && selectedTaxYear && selectedForm && isImportDataGridViewLoaded) ? (
                <ImportDataGridView
                    isStateFilingApplicable={false}
                    filingForCorrected={false}
                    formSettings={formSettings}
                    formName={selectedForm}
                    formId={
                        (() => {
                            switch (selectedForm) {
                                case INTEGRATION_CONSTANTS.NEC_FORM:
                                    return FORM_IDS.$1099NEC;
                                case INTEGRATION_CONSTANTS.MISC_FORM:
                                    return FORM_IDS.$1099MISC;
                                default:
                                    return null;
                            }
                        })()
                    }
                    taxYear={selectedTaxYear}
                />
            ) : (
                <div style={styles.container}>
                    <div style={styles.marginBottomStyle}>
                        <AccountigSuiteInstruction />
                    </div>
                    <Loadable
                        currentState={loadingState.state}
                        loadingStates={INTEGRATION_WRAPPER_LOADING_STATES.GRID_LOADING}
                        messageMap={INTEGRATION_WRAPPER_LOADING_STATES_MESSAGE_MAP}
                        isNewLoadable={true}
                    >
                        {formattedTenantList && gridColumns && (
                            <RichGrid
                                columns={gridColumns}
                                data={formattedTenantList}
                            />
                        )}
                        <div style={styles.marginBottomStyle}>
                            <Button
                                type={BUTTON_TYPE.PRIMARY}
                                title={"Link To Service"}
                                onClickCb={() => dispatch(getAccountingSuiteUrl())}
                            >
                                Link to accountingsuite
                            </Button>
                        </div>
                    </Loadable>
                </div>
            )}
        </>
    );
}